import React, { useEffect, useState } from "react";
import { ClassIcon } from "../components/ClassIcon";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import WrestlerPortrait from "../components/WrestlerPortrait";
import { useAuth } from "../lib/auth";
import { Plus, Lock, Users, Skull, HelpCircle } from "lucide-react";
import { toast } from "sonner";
import { SlotShell, TimeAgo, StateBadge, ClaimButton } from "../components/roster/RosterUI";
import { WR_BORDER } from "../components/wrestler/WrestlerUI";
import { SigningSplash } from "../components/roster/SigningSplash";
import { RickyTutorial } from "../components/RickyTutorial";

const SLOT0 = "[data-testid='create-wrestler-slot-0'],[data-testid='create-wrestler-form-0']";

function stateColor(s) {
  if (s === "training") return "#FF007F";
  if (s === "fighting") return "#FF007F";
  if (s === "promo") return "#39FF14";
  if (s === "pyro") return "#FF9500";
  if (s === "tour") return "#4D9FFF";
  return "#00F0FF";
}

function xpForLevel(lvl) {
  return 100 + (lvl - 1) * 40 + (lvl - 1) ** 2 * 18;
}

function pendingLabel(w) {
  if (w.passive_type === "promo") return `Claim ${(w.promo_pending_fans || 0).toLocaleString()} Fans`;
  if (w.passive_type === "pyro") return `Claim ${(w.pyro_pending_energy || 0).toFixed(2)} Energy`;
  if (w.passive_type === "tour") return "Claim Tour Fans";
  return "Claim Training Rewards";
}

function ClassChip({ cls, testId }) {
  return (
    <div data-testid={testId} title={cls.desc}
         className="min-w-0 px-1.5 py-1 rounded-md text-[9px] font-mono flex items-center gap-1 bg-[#0A051A]/85 backdrop-blur border"
         style={{ borderColor: cls.color + "88", color: cls.color }}>
      <ClassIcon classId={cls.id} color={cls.color} size={12} testId={`${testId}-icon`} />
      <span className="uppercase tracking-wider truncate hidden sm:inline">{cls.name}</span>
    </div>
  );
}

function FighterCard({ w, delay, onClick, fresh }) {
  const xpPct = Math.min(100, (w.xp / xpForLevel(w.level)) * 100);
  const cls = w.class_info || {};
  const sc = stateColor(w.state);
  const active = w.state && w.state !== "idle";
  return (
    <div className={`relative ${fresh ? "roster-fresh" : ""}`} data-testid={fresh ? "roster-fresh-card" : undefined}>
    {fresh && <div className="roster-fresh-tag absolute -top-3 left-1/2 -translate-x-1/2 z-20 px-3 py-0.5 rounded-full font-heading text-[10px] uppercase tracking-widest text-[#0A051A]" style={{ background: "#FFE600", boxShadow: "0 0 14px #FFE600" }}>New Signing</div>}
    <SlotShell testId={`wrestler-card-${w.wrestler_id}`} extra={{ "data-fighter-card": w.wrestler_id }} onClick={onClick} delay={delay}
               colors={active ? [sc, "#5B3BFF", sc] : WR_BORDER}>
      <div className="relative rounded-xl p-[3px] mb-2 group" style={{ background: `linear-gradient(140deg, ${active ? sc : "#00F0FF"}, #5B3BFF, #FF007F)`, boxShadow: `0 0 18px ${active ? sc + "55" : "rgba(91,59,255,0.4)"}` }}>
        <div className="aspect-square bg-[#0A051A] rounded-[10px] overflow-hidden relative">
          <WrestlerPortrait w={w} />
          <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-[#0A051A]/80 to-transparent pointer-events-none" />
          <div className="absolute top-1.5 inset-x-1.5 flex items-start justify-between gap-1">
            {cls.id ? <ClassChip cls={cls} testId={`slot-class-${w.slot_index}`} /> : <span />}
            <StateBadge state={w.state} color={sc} testId={`state-badge-${w.wrestler_id}`} iconTestId={`activity-icon-${w.wrestler_id}`} />
          </div>
          <div className="absolute bottom-1.5 left-1.5 px-2 py-0.5 rounded-md bg-black/50 backdrop-blur-sm border border-white/15">
            <span className="font-num text-lg leading-none tracking-wider neon-cyan">LV {w.level}</span>
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 mb-1">
        <div className="font-graffiti text-lg leading-tight text-white truncate flex-1 min-w-0" style={{ textShadow: "0 0 12px #FF007F88, 2px 2px 0 #000" }}>{w.name}</div>
        <span className="inline-flex items-center gap-1 font-num text-base leading-none tracking-wider text-[#FF3399] shrink-0" data-testid={`fighter-kills-${w.wrestler_id}`}>
          <Skull size={13} strokeWidth={2.6} /> {(w.total_training_kills || 0).toLocaleString()}
        </span>
      </div>
      <TimeAgo iso={w.last_interaction} testId={`time-ago-${w.wrestler_id}`} />
      <div className="xp-bar">
        <div className="xp-fill relative overflow-hidden" style={{ width: `${xpPct}%` }}><span className="wr-xp-shine absolute inset-y-0 w-1/3" /></div>
      </div>
      {w.passive_pending && <ClaimButton type={w.passive_type} label={pendingLabel(w)} testId={`claim-pill-${w.wrestler_id}`} />}
    </SlotShell>
    </div>
  );
}

const SLOT_CLASSES = [
  { id: "heavyweight",   name: "Heavyweight",   emoji: "🏋️", color: "#FF007F", desc: "+25% Power" },
  { id: "middleweight",  name: "Middleweight",  emoji: "⚡",  color: "#00F0FF", desc: "+25% Workrate" },
  { id: "cruiserweight", name: "Cruiserweight", emoji: "🎲", color: "#FFD700", desc: "+25% Fortune" },
  { id: "tag_team",      name: "Tag Team",      emoji: "🤝", color: "#39FF14", desc: "+20% Together Bonus w/ partner on same activity" },
  { id: "wildcard",      name: "Wildcard",      emoji: "🃏", color: "#9D00FF", desc: "×1.5 active rewards · picks division" },
];

function SlotArt({ cls, icon: Icon, dashed, dim }) {
  const c = cls?.color || "#695D8E";
  return (
    <div className={`aspect-square rounded-xl bg-[#0A051A]/80 border-2 ${dashed ? "border-dashed" : ""} mb-2 flex flex-col items-center justify-center`} style={{ borderColor: dim ? "#2D1C52" : c + "88" }}>
      <Icon size={40} strokeWidth={2.6} className={dim ? "" : "ro-pulse"} style={{ color: dim ? "#3D2A5A" : c, "--c": c }} />
      {cls && (
        <div className={`mt-2 flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider ${dim ? "opacity-70" : ""}`} style={{ color: c }}>
          <ClassIcon classId={cls.id} color={c} size={12} />{cls.name}
        </div>
      )}
    </div>
  );
}

function LockedSlotCard({ slot, cost, onUnlock, delay }) {
  const cls = SLOT_CLASSES[slot];
  return (
    <SlotShell testId={`locked-slot-${slot}`} delay={delay} colors={[cls.color + "AA", "#2D1C52", cls.color + "55"]}>
      <SlotArt cls={cls} icon={Lock} dashed />
      <div className="font-graffiti text-base mb-1.5 text-[#C9BDF0]">Slot {slot + 1}</div>
      <button data-testid={`unlock-slot-btn-${slot}`} onClick={onUnlock} className="btn-cyan w-full text-[10px] px-2 py-1.5">
        {cost} Handshakes
      </button>
    </SlotShell>
  );
}

function EmptySlotCard({ slot, onCreate, delay }) {
  const cls = SLOT_CLASSES[slot];
  const [name, setName] = useState("");
  const [open, setOpen] = useState(false);
  const c = cls?.color || "#FF007F";
  if (!open) {
    return (
      <SlotShell testId={`create-wrestler-slot-${slot}`} onClick={() => setOpen(true)} delay={delay} colors={[c, "#5B3BFF", c]}>
        <SlotArt cls={cls} icon={Plus} dashed />
        <div className="font-graffiti text-base text-center" style={{ color: c, textShadow: `0 0 10px ${c}88, 2px 2px 0 #000` }}>Create Wrestler</div>
      </SlotShell>
    );
  }
  return (
    <SlotShell testId={`create-wrestler-form-${slot}`} delay={0} colors={[c, "#5B3BFF", c]}>
      <form onSubmit={(e) => { e.preventDefault(); onCreate(name, slot); setName(""); setOpen(false); }}>
        <SlotArt cls={cls} icon={Plus} />
        <input
          autoFocus
          data-testid={`new-wrestler-name-input-${slot}`}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Name"
          maxLength={20}
          className="w-full px-2 py-1.5 rounded-md bg-[#0A051A] border border-[#FF007F]/40 focus:border-[#00F0FF] outline-none font-heading text-xs uppercase"
        />
        <button data-testid={`create-wrestler-submit-${slot}`} type="submit" className="btn-neon w-full mt-1.5 text-[10px] px-2 py-1.5">CREATE</button>
      </form>
    </SlotShell>
  );
}

function MysterySlotCard({ slot, delay }) {
  const cls = SLOT_CLASSES[slot];
  return (
    <SlotShell testId={`mystery-slot-${slot}`} delay={delay} colors={["#2D1C52", "#1C0D40", "#2D1C52"]}>
      <div className="opacity-60">
        <SlotArt cls={cls} icon={HelpCircle} dashed dim />
        <div className="font-graffiti text-base text-[#695D8E]">Slot {slot + 1}</div>
        <div className="text-[10px] text-[#695D8E] font-mono">Unlock earlier first</div>
      </div>
    </SlotShell>
  );
}

export default function RosterPage() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [wrestlers, setWrestlers] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    const r = await api.get("/wrestlers");
    setWrestlers(r.data);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  // Passive promo claim ticker (training passive accumulator = Batch C)
  useEffect(() => {
    const id = setInterval(async () => {
      for (const w of wrestlers) {
        if (w.state === "promo") {
          try { await api.post(`/wrestlers/${w.wrestler_id}/promo/passive-claim`); } catch {}
        }
      }
      load();
      refresh();
    }, 5000);
    return () => clearInterval(id);
  }, [wrestlers.length, wrestlers.map(w => w.state).join(",")]);

  const [signed, setSigned] = useState(null);
  const [freshId, setFreshId] = useState(null);
  const closeSplash = React.useCallback(() => setSigned(null), []);

  const create = async (name, slot) => {
    if (!name.trim()) return;
    try {
      const r = await api.post("/wrestlers", { name, slot_index: slot });
      await load();
      if (user?.tutorial_stage === "roster") { await api.post("/tutorial/advance", { stage: "created" }); refresh(); }
      setSigned(r.data);
      setFreshId(r.data.wrestler_id);
      setTimeout(() => setFreshId(null), 6000);
      window.dispatchEvent(new CustomEvent("bulk-cheer"));
      toast.success(`${name} joined the roster!`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const unlock = async () => {
    try {
      await api.post("/wrestlers/unlock-slot");
      await refresh();
      toast.success("Slot unlocked!");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  if (loading) return <div className="p-8 text-center font-graffiti text-2xl neon-cyan">LOADING ROSTER...</div>;

  const extraSlots = user?.extra_slots || 0;
  const maxSlots = Math.min(5, 1 + extraSlots);
  const nextUnlockCost = 5 * (extraSlots + 1);

  const wrestlerBySlot = {};
  wrestlers.forEach((w, i) => {
    const slot = (typeof w.slot_index === "number") ? w.slot_index : i;
    wrestlerBySlot[slot] = w;
  });

  return (
    <>
      <div className="fixed inset-0 z-0 pointer-events-none bg-cover bg-center" data-testid="roster-page-bg" aria-hidden="true"
           style={{ backgroundImage: "linear-gradient(180deg, rgba(10,5,26,0.55), rgba(10,5,26,0.82)), url('/backgroundgraphics/pvpshowspage_background.webp')" }} />
      <div className="relative z-[1] max-w-[1600px] mx-auto px-4 py-5" data-testid="roster-page">
        <div className="mb-5 wr-rise">
          <div className="flex items-center gap-3">
            <Users size={34} strokeWidth={2.6} className="text-[#00F0FF] shrink-0" style={{ filter: "drop-shadow(0 0 8px #00F0FF)" }} />
            <h1 className="font-graffiti text-4xl sm:text-5xl leading-none uppercase" data-testid="roster-title">
              <span className="text-[#FF007F]" style={{ textShadow: "0 0 16px #FF007F99, 3px 3px 0 #000" }}>Your</span>{" "}
              <span className="text-[#00F0FF]" style={{ textShadow: "0 0 16px #00F0FF99, 3px 3px 0 #000" }}>Roster</span>
            </h1>
          </div>
          <div className="mt-2 h-[3px] max-w-xl rounded-full" style={{ background: "linear-gradient(90deg, #FF007F, #00F0FF 60%, transparent)", boxShadow: "0 0 10px #FF007F88" }} />
          <p className="mt-2 text-[#C9BDF0] font-mono text-xs tracking-wide">
            <span className="font-num text-base tracking-wider text-[#00F0FF]">{wrestlers.length} / 5</span> wrestlers · click a card to manage · each slot has a unique class
          </p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
          {[0, 1, 2, 3, 4].map((slot) => {
            const w = wrestlerBySlot[slot];
            const delay = slot * 90;
            if (w) return <FighterCard key={`slot-${slot}`} w={w} delay={delay} fresh={freshId === w.wrestler_id} onClick={async () => {
              if (user?.tutorial_stage === "created") { try { await api.post("/tutorial/advance", { stage: "wrestler" }); await refresh(); } catch {} }
              nav(`/wrestler/${w.wrestler_id}`);
            }} />;
            if (slot < maxSlots) return <EmptySlotCard key={`slot-${slot}`} slot={slot} delay={delay} onCreate={create} />;
            if (slot === maxSlots && maxSlots < 5) return <LockedSlotCard key={`slot-${slot}`} slot={slot} delay={delay} cost={nextUnlockCost} onUnlock={unlock} />;
            return <MysterySlotCard key={`slot-${slot}`} slot={slot} delay={delay} />;
          })}
        </div>
      </div>
      {signed && <SigningSplash w={signed} onClose={closeSplash} />}
      {!signed && user?.tutorial_stage === "roster" && (
        <RickyTutorial holdTarget={SLOT0}
          steps={[
            { text: "OWWWWW, MY BACK!" },
            { text: "I’ve broken hearts in every city. These days, I break into a sweat getting out of the limousine!" },
            { text: "Bright lights, big cities, beautiful women… It’s time to grab that brass ring, rookie!" },
            { text: "Build me a superstar that makes the ladies swoon", target: SLOT0 },
          ]} />
      )}
      {!signed && user?.tutorial_stage === "created" && (
        <RickyTutorial key="created" holdTarget="[data-testid^='wrestler-card-']"
          steps={[{ text: "Well, look at that! Now click on the handsome devil!", target: "[data-testid^='wrestler-card-']" }]} />
      )}
    </>
  );
}
